import {Box, Toolbar, useMediaQuery, useTheme} from '@mui/material';
import React from 'react';
import {Outlet} from 'react-router';
import {navigation} from '../../theme/tokens';
import NavRail from './NavRail';

type AppLayoutProps = {
  children?: React.ReactNode;
};

const AppLayout: React.FC<AppLayoutProps> = ({children}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // State for rail expansion (desktop only)
  const [railExpanded, setRailExpanded] = React.useState(() => {
    try {
      const saved = localStorage.getItem('navRailExpanded');
      if (saved !== null) {
        return JSON.parse(saved) === true;
      }
    } catch {
      // Storage unavailable (Safari with cookies blocked exposes it as null)
      // or the stored value is corrupt — fall through to the default.
    }
    // Default to collapsed on tablet, expanded on desktop
    return !isTablet;
  });

  // Save preference
  React.useEffect(() => {
    try {
      localStorage.setItem('navRailExpanded', JSON.stringify(railExpanded));
    } catch {
      // Storage unavailable — the choice still applies for this visit.
    }
  }, [railExpanded]);

  const handleRailToggle = React.useCallback(() => {
    setRailExpanded((prev: boolean) => !prev);
  }, []);

  const railWidth = railExpanded
    ? navigation.railExpandedWidth
    : navigation.railWidth;

  // Mobile layout: content only (AppBar and BottomNav rendered by AppContainer)
  if (isMobile) {
    return (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: {xs: 2, sm: 2},
        }}>
        {children || <Outlet />}
      </Box>
    );
  }

  // Desktop/Tablet layout: NavRail + content
  return (
    <Box sx={{display: 'flex', minHeight: '100vh'}}>
      {/* NavRail */}
      <NavRail expanded={railExpanded} onToggle={handleRailToggle} />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: `calc(100% - ${railWidth}px)`,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}>
        {/* AppBar spacer */}
        <Toolbar />

        {/* Content */}
        <Box sx={{p: {xs: 2, md: 3}}}>{children || <Outlet />}</Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
